import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { Platform } from '@angular/cdk/platform';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';
import { NativeDateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { DateAdapter } from 'angular-calendar';
import { NgxsModule, NgxsModuleOptions, Store } from '@ngxs/store';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { InputAssistantUserAutocompleteComponent } from '@nimbus-assistant/components/outbound/assistant-user-autocomplete/input-assistant-user-autocomplete.component';
import {
  EnvironmentConfigurationService,
  ENVIRONMENT_CONFIGURATION_VARIABLE,
  RELEASE_VERSION_CONFIGURATION_VARIABLE,
} from '@nimbus-library/features/configuration';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { IconParametersService } from '@nimbus-library/features/shared/services/icon-parameters.service';
import { DefaultIconParametersService } from '@nimbus-library/features/shared/services/ui/default-icon-parameters.service';
import { LocalizationModule } from '@nimbus-library/features/localization/localization.module';
import { ModalModule } from '@nimbus-library/features/modal/modal.module';
import { NimbusDateFormats } from '@nimbus-library/features/localization/services/nimbus-date-adapter';
import { CallAsDialComponent } from '@nimbus-global/features/call-as/call-as-dial/call-as-dial.component';
import { IUserTasksCapacityService } from '@nimbus-global/features/user-tasks-capacity/services/i-user-tasks-capacity.service';
import { UserTpCapacityButtonComponent } from '@nimbus-global/features/user-tasks-capacity/components/user-tp-capacity-button/user-tp-capacity-button.component';
import { TrackingModule } from '@nimbus-library/features/tracking/tracking.module';
import { LoggingModule } from '@nimbus-library/features/logger/logging.module';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';
import { IEnvironmentService } from '@nimbus-library/features/configuration/i-environment.service';
import { ApiQueryParamsInterceptor } from '@nimbus-library/features/shared/interceptors/api-query-params.interceptor';
import { environment } from '../environments/environment';
import { EnvironmentAssistantService } from '../environments/environment-assistant.service';
import { AssistantUserTasksCapacityService } from './services/assistant-user-tasks-capacity.service';
import { ApiInterceptor } from './interceptors/api.interceptor';
import { LocalizationService } from './localization/localization.service';
import { InfoComponent } from './components/info/info.component';
import { ErrorComponent } from './components/error/error.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MSALInterceptorConfigFactory, MSALGuardConfigFactory, MSALInstanceFactory } from './auth-config';
import { HeaderComponent } from './components/header/header.component';
import { LoginComponent } from './components/login/login.component';
import { NotAuthGuard } from './services/guards/auth-not.guard';
import { GraphApiService } from './services/graph-api.service';
import { SettingsComponent } from './components/settings/settings.component';
import { MsalRedirectComponent } from './components/msal-redirect/msal-redirect.component';
import { OutboundComponent } from './components/outbound/outbound.component';
import { NotAvailableReasonComponent } from './components/not-available-reason/not-available-reason.component';
import { TrackingConfigurationService } from './services/tracking/tracking-configuration.service';
import { LoggingService } from './services/logging.service';
import { SettingsGeneralTabComponent } from './components/settings/settings-general-tab/settings-general-tab.component';
import { SettingsNotificationsTabComponent } from './components/settings/settings-notification-tab/settings-notifications-tab.component';
import { SettingsAppearanceTabComponent } from './components/settings/settings-appearance-tab/settings-appearance-tab.component';

export const ngxsConfig: NgxsModuleOptions = {
  selectorOptions: {
    suppressErrors: false,
    injectContainerState: false,
  },
};

@NgModule({
  declarations: [
    AppComponent,

    CallAsDialComponent,
    ErrorComponent,

    InfoComponent,
    LoginComponent,
    MsalRedirectComponent,
    NotAvailableReasonComponent,
    OutboundComponent,

    SettingsComponent,
    SettingsAppearanceTabComponent,
    SettingsGeneralTabComponent,
    SettingsNotificationsTabComponent,
    InputAssistantUserAutocompleteComponent,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    LoggingModule.forRoot(
      {
        loggingConfiguration: {
          cloudLoggingEnabled: environment.production,
          consoleLoggingEnabled: true,
          zipkinLoggingEnabled: false,
        },
      },
      'Nimbus.UI.Assistant',
    ),
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatInputModule,
    MatMenuModule,
    MatNativeDateModule,
    MatSelectModule,
    ModalModule,
    MsalModule,
    NgxsModule.forRoot([], ngxsConfig),
    ReactiveFormsModule,
    SharedUIModule,
    HeaderComponent,
    UserTpCapacityButtonComponent,
    LocalizationModule.forRoot(LocalizationService, {
      fileSuffix: '.json',
      folderPrefix: ['i18n/'],
    }),
    TrackingModule.forRoot(TrackingConfigurationService),
  ],
  providers: [
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiQueryParamsInterceptor, multi: true },
    {
      provide: MSAL_INSTANCE,
      deps: [EnvironmentConfigurationService, LoggingService],
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      useFactory: (confService: EnvironmentConfigurationService, loggingService: LoggingService) =>
        MSALInstanceFactory(confService, loggingService),
    },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
    { provide: DateAdapter, useClass: NativeDateAdapter, deps: [MAT_DATE_LOCALE, Platform, Store] },
    { provide: MAT_DATE_FORMATS, useValue: NimbusDateFormats },
    { provide: IconParametersService, useClass: DefaultIconParametersService },
    { provide: IEnvironmentService, useClass: EnvironmentAssistantService },
    { provide: ENVIRONMENT_CONFIGURATION_VARIABLE, useValue: 'EnvironmentConfiguration' },
    { provide: RELEASE_VERSION_CONFIGURATION_VARIABLE, useValue: 'ReleaseVersion' },
    { provide: IMicrosoftTeamsContextAccessor, useValue: null },
    { provide: IUserTasksCapacityService, useClass: AssistantUserTasksCapacityService },
    EnvironmentConfigurationService,
    FormGroupDirective,
    LoggingService,
    GraphApiService,
    MsalGuard,
    MsalBroadcastService,
    MsalService,
    NotAuthGuard,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
